import React, { useState } from 'react';
import { Calendar, UserCheck, Shield } from 'lucide-react';
import { AppShell } from '../../components/layout/AppShell';
import { PageLayout } from '../../components/common/PageLayout';
import { useHolidayPermissions } from './hooks';
import { MyRequestsTab, AdminTab, BankHolidaysTab, OverviewTab } from './tabs';

export const HolidaysPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'my-requests' | 'admin' | 'bank-holidays' | 'overview'>('my-requests');
  const { canView, canRequest, canManage } = useHolidayPermissions();

  const tabs = [
    {
      id: 'my-requests' as const,
      label: 'My Requests',
      icon: Calendar,
      show: canView || canRequest,
    },
    {
      id: 'overview' as const,
      label: 'Overview',
      icon: UserCheck,
      show: canManage,
    },
    {
      id: 'admin' as const,
      label: 'Admin',
      icon: UserCheck,
      show: canManage,
    },
    {
      id: 'bank-holidays' as const,
      label: 'Bank Holidays',
      icon: Shield,
      show: canManage,
    },
  ].filter(tab => tab.show);

  return (
    <AppShell>
      <PageLayout
        title="Holidays"
        subtitle="Manage employee holidays and time off"
        bgColor="bg-amber-50"
        textColor="text-amber-900"
        borderColor="border-amber-200"
        icon={<Calendar className="w-6 h-6" />}
      >
        {/* Tab Navigation */}
        {tabs.length > 1 && (
          <div className="mb-6">
            <nav className="flex space-x-1 bg-white rounded-lg p-1 shadow-sm border border-gray-200">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                      isActive
                        ? 'bg-amber-100 text-amber-800'
                        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {tab.label}
                  </button>
                );
              })}
            </nav>
          </div>
        )}

        {/* Tab Content */}
        <div>
          {activeTab === 'my-requests' && <MyRequestsTab />}
          {activeTab === 'overview' && <OverviewTab />}
          {activeTab === 'admin' && <AdminTab />}
          {activeTab === 'bank-holidays' && <BankHolidaysTab />}
        </div>
      </PageLayout>
    </AppShell>
  );
};

export default HolidaysPage;
